@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalTime::class)

package com.wintermar.inspection.core.ui.component.datetime.util

import androidx.compose.material3.DatePickerState
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.TimePickerState
import kotlin.time.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format.MonthNames
import kotlinx.datetime.format.Padding
import kotlinx.datetime.format.char
import kotlinx.datetime.number
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

object DateTimeUtil {
    private val _isoFormat = LocalDateTime.Format {
        year()
        char('-')
        monthNumber()
        char('-')
        day()
        char('T')
        hour()
        char(':')
        minute()
        char(':')
        second()
        char('.')
        secondFraction(3) // Forces exactly 3 digits (milliseconds)
    }

    /**
     * Creates an ISO 8601 formatted date-time string in UTC from the selected date and time states.
     * The format is "yyyy-MM-ddTHH:mm:ss.SSSZ".
     *
     * @param dateState The DatePickerState containing the selected date.
     * @param timeState The TimePickerState containing the selected time.
     * @return The ISO UTC+0 formatted string or null if no date is selected.
     */
    fun getIsoUtc0DateTimeString(
        dateState: DatePickerState,
        timeState: TimePickerState,
    ): String? {
        val localTimeZone: TimeZone = TimeZone.currentSystemDefault()
        val selectedMillis = dateState.selectedDateMillis ?: return null
        val instantStartOfDay = Instant.fromEpochMilliseconds(selectedMillis)
        val localDate = instantStartOfDay.toLocalDateTime(localTimeZone).date
        val localDateTime = LocalDateTime(
            year = localDate.year,
            month = localDate.month.number,
            day = localDate.day,
            hour = timeState.hour,
            minute = timeState.minute,
            second = 0,
            nanosecond = 0
        )
        val utcInstant = localDateTime.toInstant(localTimeZone)
        return "${_isoFormat.format(utcInstant.toLocalDateTime(TimeZone.UTC))}Z"
    }

    /**
     * Converts an ISO 8601 date-time string (in UTC) to a display-friendly date string in the local device timezone.
     * The output format is "dd MMM yyyy" (e.g., "05 Jan 2026").
     *
     * @param isoUtc0String The ISO formatted date-time string (e.g., "2026-02-13T06:11:30.885Z").
     * @return The formatted date string in local time, or empty string if input is blank.
     */
    fun getLocalSystemDisplay(isoUtc0String: String): String {
        if (isoUtc0String.isBlank() || isoUtc0String == "null") return ""

        val instant = Instant.parse(isoUtc0String)
        val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())

        //Define the custom format: "example: 05 Jan 2026"
        val customFormat = LocalDate.Format {
            this@Format.day(padding = Padding.ZERO)
            char(' ')
            monthName(MonthNames.ENGLISH_ABBREVIATED)
            char(' ')
            year()
        }
        return customFormat.format(localDateTime.date)
    }

    /**
     * Converts an ISO 8601 date-time string (in UTC) to a display-friendly date and time string in the local device timezone.
     * The output format is "dd MMM yyyy, HH:mm" (e.g., "05 Jan 2026, 14:11").
     *
     * @param isoUtc0String The ISO formatted date-time string (e.g., "2026-02-13T06:11:30.885Z").
     * @return The formatted date and time string in local time, or empty string if input is blank.
     */
    fun getLocalSystemDisplayWithTime(isoUtc0String: String): String {
        if (isoUtc0String.isBlank() || isoUtc0String == "null") return ""
        val datePart = getLocalSystemDisplay(isoUtc0String)

        val instant = Instant.parse(isoUtc0String)
        val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())

        val hour = localDateTime.hour.toString().padStart(2, '0')
        val minute = localDateTime.minute.toString().padStart(2, '0')

        return "$datePart, $hour:$minute"
    }

    /**
     * Returns the current date and time in ISO 8601 format in UTC.
     * The format is "yyyy-MM-ddTHH:mm:ss.SSSZ".
     *
     * @return The current ISO formatted date-time string in UTC+0.
     */
    fun getCurrentDateIsoUtc0(): String {
        val now = Clock.System.now().toLocalDateTime(TimeZone.UTC)
        return _isoFormat.format(now) + "Z"
    }
}
